import { useState, useEffect } from 'react';
import {
  Heart,
  Star,
  Utensils,
  Building2,
  PartyPopper,
  Users,
  Send,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  MapPin,
  Phone,
  Mail,
  Camera,
  Sparkles,
} from 'lucide-react';
import { createInquiry } from '../api/inquiries';
import { useInstagramFeed } from '../hooks/useInstagramFeed';
import { RevealOnScroll } from '../components/landing/RevealOnScroll';
import { InstagramSection } from '../components/landing/InstagramSection';
import { SocialLinks } from '../components/landing/SocialLinks';

// ─── Static Data ────────────────────────────────────────────────────────────

const EVENT_TYPES = [
  { icon: Heart, label: 'Weddings', description: 'Elegant celebrations for your special day' },
  { icon: Star, label: 'Galas', description: 'Grand events with sophisticated flair' },
  {
    icon: Utensils,
    label: 'Shabbat Dinners',
    description: 'Warm, meaningful communal experiences',
  },
  {
    icon: Building2,
    label: 'Corporate Events',
    description: 'Professional gatherings that impress',
  },
  {
    icon: PartyPopper,
    label: 'Bar/Bat Mitzvahs',
    description: 'Memorable milestone celebrations',
  },
  { icon: Users, label: 'Private Parties', description: 'Personalized events for every occasion' },
];

const TESTIMONIALS = [
  {
    name: 'Sarah & David K.',
    event: 'Wedding',
    quote:
      'NP Productions made our wedding absolutely magical. Every detail was perfect, from the ceremony to the last dance.',
  },
  {
    name: 'Rabbi Michael L.',
    event: 'Community Gala',
    quote:
      'Professional, organized, and creative. They transformed our annual gala into an unforgettable evening.',
  },
  {
    name: 'Rachel G.',
    event: 'Bat Mitzvah',
    quote:
      'They understood our vision and executed it beautifully. Our daughter felt like a princess.',
  },
];

const GALLERY_PLACEHOLDERS = [
  { id: 1, alt: 'Elegant wedding setup', color: 'from-primary-400 to-primary-600' },
  { id: 2, alt: 'Gala dinner table', color: 'from-amber-400 to-orange-500' },
  { id: 3, alt: 'Beautiful ceremony', color: 'from-pink-400 to-rose-500' },
  { id: 4, alt: 'Event lighting', color: 'from-primary-300 to-primary-500' },
  { id: 5, alt: 'Table decorations', color: 'from-emerald-400 to-teal-500' },
  { id: 6, alt: 'Dance floor', color: 'from-primary-500 to-primary-700' },
];

const STATS = [
  { value: '10+', label: 'Years Experience' },
  { value: '100+', label: 'Events Produced' },
  { value: '50+', label: 'Venue Partners' },
];

// ─── Types ──────────────────────────────────────────────────────────────────

interface PublicPhoto {
  id: string;
  fileName: string;
  s3Key: string;
  caption: string | null;
  showOnLanding: boolean;
  createdAt: string;
  url: string;
}

interface PublicVenue {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  capacity: number | null;
}

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  eventType: string;
  estimatedDate: string;
  estimatedGuestCount: string;
  message: string;
}

const INITIAL_FORM: ContactFormData = {
  name: '',
  email: '',
  phone: '',
  eventType: '',
  estimatedDate: '',
  estimatedGuestCount: '',
  message: '',
};

// ─── Component ──────────────────────────────────────────────────────────────

export function LandingPage() {
  const [form, setForm] = useState<ContactFormData>(INITIAL_FORM);
  const [submitState, setSubmitState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [photos, setPhotos] = useState<PublicPhoto[]>([]);
  const [venues, setVenues] = useState<PublicVenue[]>([]);
  const { feed: instagramFeed, loading: instagramLoading } = useInstagramFeed();
  const instagramUrl = instagramFeed?.profileUrl ?? null;

  useEffect(() => {
    fetch('/api/v1/inquiries/public-photos')
      .then((res) => (res.ok ? res.json() : []))
      .then((data: PublicPhoto[]) => setPhotos(data))
      .catch(() => setPhotos([]));

    fetch('/api/v1/inquiries/public-venues')
      .then((res) => (res.ok ? res.json() : []))
      .then((data: PublicVenue[]) => setVenues(data))
      .catch(() => setVenues([]));
  }, []);

  const landingPhotos = photos.filter((p) => p.showOnLanding);
  const galleryPhotos = photos.filter((p) => !p.showOnLanding);
  const heroImage = landingPhotos.length > 0 ? landingPhotos[0].url : null;
  const GALLERY_DISPLAY_COUNT = 6;
  const displayGallery = galleryPhotos.slice(0, GALLERY_DISPLAY_COUNT);
  const hasMorePhotos = galleryPhotos.length > GALLERY_DISPLAY_COUNT;

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitState('loading');
    setErrorMessage('');

    try {
      await createInquiry({
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        eventType: form.eventType || undefined,
        estimatedDate: form.estimatedDate || undefined,
        estimatedGuestCount: form.estimatedGuestCount
          ? parseInt(form.estimatedGuestCount, 10)
          : undefined,
        message: form.message || undefined,
      });
      setSubmitState('success');
      setForm(INITIAL_FORM);
    } catch {
      setSubmitState('error');
      setErrorMessage('Something went wrong. Please try again or contact us directly.');
    }
  }

  return (
    <main>
      {/* Hero Section */}
      <section
        aria-label="Hero"
        className="relative overflow-hidden text-white"
        style={
          heroImage
            ? {
                backgroundImage: `url(${heroImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : undefined
        }
      >
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 opacity-90" />
        {/* Animated accent */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 via-transparent to-primary-600/20 animate-pulse" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-36 lg:py-44">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-sm font-medium text-primary-100 backdrop-blur">
              <Sparkles className="h-4 w-4" />
              South Florida&apos;s Premier Event Production
            </div>
            <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-tight">
              Crafting{' '}
              <span className="bg-gradient-to-r from-amber-300 to-pink-300 bg-clip-text text-transparent">
                Unforgettable
              </span>{' '}
              Events
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-primary-100 max-w-2xl">
              South Florida&apos;s premier event production company, specializing in Jewish
              lifecycle celebrations. From intimate Shabbat dinners to grand weddings, we bring your
              vision to life.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#contact"
                className="inline-flex items-center px-8 py-4 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 transition-colors shadow-lg shadow-primary-900/30"
              >
                Get Started
                <ChevronRight className="ml-2 h-5 w-5" />
              </a>
              <a
                href="#services"
                className="inline-flex items-center px-8 py-4 rounded-lg border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors"
              >
                Our Services
              </a>
            </div>

            {/* Stats strip */}
            <div className="mt-14 flex flex-wrap gap-8 sm:gap-12">
              {STATS.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-white">{stat.value}</div>
                  <div className="mt-1 text-sm text-primary-200">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" aria-label="Services" className="py-20 sm:py-24 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealOnScroll className="text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Our Services</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              We specialize in creating extraordinary events tailored to your unique vision and
              traditions.
            </p>
          </RevealOnScroll>
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {EVENT_TYPES.map((service, index) => (
              <RevealOnScroll key={service.label} delay={index * 75}>
                <div className="group relative h-full p-8 bg-primary-50 rounded-2xl hover:bg-primary-900 hover:text-white transition-all duration-300 border border-primary-200 hover:border-primary-900 hover:-translate-y-1 hover:shadow-xl">
                  <service.icon className="h-10 w-10 text-primary-600 group-hover:text-white transition-colors" />
                  <h3 className="mt-4 text-xl font-semibold">{service.label}</h3>
                  <p className="mt-2 text-gray-600 group-hover:text-primary-200 transition-colors">
                    {service.description}
                  </p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" aria-label="About" className="py-20 sm:py-24 bg-primary-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealOnScroll className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <article>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">About NP Productions</h2>
              <p className="mt-6 text-lg text-gray-600">
                With years of experience in South Florida&apos;s vibrant Jewish community, NP
                Productions brings passion, precision, and creativity to every event. We understand
                the traditions, customs, and attention to detail that make each celebration truly
                special.
              </p>
              <p className="mt-4 text-lg text-gray-600">
                From coordinating with vendors to managing every timeline detail, we ensure your
                event runs seamlessly so you can focus on what matters most — celebrating with your
                loved ones.
              </p>
            </article>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-primary-200">
                <div className="text-4xl font-bold text-primary-600">100+</div>
                <div className="mt-2 text-sm text-gray-600">Events Produced</div>
              </div>
              <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-primary-200">
                <div className="text-4xl font-bold text-primary-600">10+</div>
                <div className="mt-2 text-sm text-gray-600">Years Experience</div>
              </div>
              <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-primary-200">
                <div className="text-4xl font-bold text-primary-600">50+</div>
                <div className="mt-2 text-sm text-gray-600">Venue Partners</div>
              </div>
              <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-primary-200">
                <div className="text-4xl font-bold text-primary-600">100%</div>
                <div className="mt-2 text-sm text-gray-600">Client Satisfaction</div>
              </div>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* Gallery Section — Landing Photos */}
      {landingPhotos.length > 0 && (
        <section aria-label="Featured photos" className="py-20 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Featured Moments</h2>
              <p className="mt-4 text-lg text-gray-600">Highlights from our recent events</p>
            </div>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {landingPhotos.map((photo) => (
                <div key={photo.id} className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img
                    src={photo.url}
                    alt={photo.caption || 'Event photo'}
                    loading="lazy"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery Section — All Public Photos or Placeholders */}
      <section
        aria-label="Photo gallery"
        className={`py-20 sm:py-24 ${landingPhotos.length > 0 ? 'bg-primary-50' : 'bg-white'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Gallery</h2>
            <p className="mt-4 text-lg text-gray-600">A glimpse of our recent events</p>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4">
            {displayGallery.length > 0
              ? displayGallery.map((photo) => (
                  <div key={photo.id} className="aspect-[4/3] rounded-xl overflow-hidden">
                    <img
                      src={photo.url}
                      alt={photo.caption || 'Event photo'}
                      loading="lazy"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))
              : GALLERY_PLACEHOLDERS.map((item) => (
                  <div
                    key={item.id}
                    className={`aspect-[4/3] rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center`}
                  >
                    <Camera className="h-8 w-8 text-white/50" />
                    <span className="sr-only">{item.alt}</span>
                  </div>
                ))}
          </div>
          {hasMorePhotos && (
            <div className="mt-8 text-center">
              <a
                href="#contact"
                className="inline-flex items-center text-primary-600 font-semibold hover:text-primary-700 transition-colors"
              >
                View Full Gallery
                <ChevronRight className="ml-1 h-5 w-5" />
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Instagram Section — live feed */}
      <InstagramSection feed={instagramFeed} loading={instagramLoading} />

      {/* Venues Section */}
      {venues.length > 0 && (
        <section aria-label="Venues" className="py-20 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Venues We Work With</h2>
              <p className="mt-4 text-lg text-gray-600">
                Trusted partnerships with South Florida&apos;s finest event spaces
              </p>
            </div>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {venues.map((venue) => (
                <div
                  key={venue.id}
                  className="rounded-2xl border border-primary-200 bg-primary-50 p-6 hover:shadow-md transition-shadow"
                >
                  <Building2 className="h-8 w-8 text-primary-600 mb-3" />
                  <h3 className="text-lg font-semibold text-gray-900">{venue.name}</h3>
                  {(venue.city || venue.state) && (
                    <p className="mt-1 text-sm text-gray-500 flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {[venue.city, venue.state].filter(Boolean).join(', ')}
                    </p>
                  )}
                  {venue.capacity && (
                    <p className="mt-1 text-sm text-gray-500 flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      Up to {venue.capacity} guests
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      <section
        aria-label="Testimonials"
        className={`py-20 sm:py-24 ${venues.length > 0 ? 'bg-primary-50' : 'bg-primary-50'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">What Our Clients Say</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((testimonial) => (
              <article
                key={testimonial.name}
                className="bg-white rounded-2xl p-8 shadow-sm border border-primary-200"
              >
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="h-5 w-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-gray-600 italic">&ldquo;{testimonial.quote}&rdquo;</p>
                <div className="mt-6">
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-primary-600">{testimonial.event}</div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" aria-label="Contact" className="py-20 sm:py-24 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                Let&apos;s Plan Your Event
              </h2>
              <p className="mt-4 text-lg text-gray-600">
                Tell us about your vision and we&apos;ll help bring it to life. Fill out the form
                and we&apos;ll get back to you within 24 hours.
              </p>
              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-3 text-gray-600">
                  <MapPin className="h-5 w-5 text-primary-600 shrink-0" />
                  <span>South Florida</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <Phone className="h-5 w-5 text-primary-600 shrink-0" />
                  <span>Contact us for details</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <Mail className="h-5 w-5 text-primary-600 shrink-0" />
                  <span>info@np-productions.com</span>
                </div>
              </div>
            </div>
            <div>
              {submitState === 'success' ? (
                <div
                  className="bg-green-50 rounded-2xl p-8 text-center"
                  data-testid="success-message"
                >
                  <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
                  <h3 className="mt-4 text-xl font-semibold text-gray-900">Thank You!</h3>
                  <p className="mt-2 text-gray-600">
                    Your inquiry has been submitted. We&apos;ll be in touch soon.
                  </p>
                  <button
                    onClick={() => setSubmitState('idle')}
                    className="mt-6 text-sm text-primary-600 underline hover:no-underline"
                  >
                    Send another inquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  {submitState === 'error' && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      {errorMessage}
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={form.name}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="eventType"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Event Type
                      </label>
                      <select
                        id="eventType"
                        name="eventType"
                        value={form.eventType}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition bg-white"
                      >
                        <option value="">Select an event type</option>
                        <option value="Wedding">Wedding</option>
                        <option value="Gala">Gala</option>
                        <option value="Shabbat Dinner">Shabbat Dinner</option>
                        <option value="Corporate Event">Corporate Event</option>
                        <option value="Bar/Bat Mitzvah">Bar/Bat Mitzvah</option>
                        <option value="Private Party">Private Party</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label
                        htmlFor="estimatedDate"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Estimated Date
                      </label>
                      <input
                        type="date"
                        id="estimatedDate"
                        name="estimatedDate"
                        value={form.estimatedDate}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="estimatedGuestCount"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Estimated Guests
                      </label>
                      <input
                        type="number"
                        id="estimatedGuestCount"
                        name="estimatedGuestCount"
                        min="1"
                        value={form.estimatedGuestCount}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition"
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      value={form.message}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition resize-none"
                      placeholder="Tell us about your event..."
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitState === 'loading'}
                    className="w-full inline-flex items-center justify-center px-6 py-3 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitState === 'loading' ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Send Inquiry
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-3">
                <img
                  src="/logo.png"
                  alt="NP Productions logo"
                  className="h-10 w-10 rounded bg-white p-1"
                />
                <div>
                  <div className="font-bold">NP Productions</div>
                  <div className="text-xs text-primary-300">Events & Production</div>
                </div>
              </div>
              <p className="mt-4 text-sm text-primary-300">
                Creating unforgettable events in South Florida&apos;s Jewish community.
              </p>
              <SocialLinks
                instagramUrl={instagramUrl}
                className="mt-4"
                linkClassName="h-9 w-9 rounded-full bg-primary-800 text-white hover:bg-primary-700"
              />
            </div>
            <div>
              <h3 className="font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm text-primary-300">
                <li>
                  <a href="#services" className="hover:text-white transition-colors">
                    Services
                  </a>
                </li>
                <li>
                  <a href="#about" className="hover:text-white transition-colors">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="#contact" className="hover:text-white transition-colors">
                    Contact
                  </a>
                </li>
                {instagramUrl && (
                  <li>
                    <a
                      href={instagramUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-white transition-colors"
                    >
                      Instagram
                    </a>
                  </li>
                )}
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-sm text-primary-300">
                <li>South Florida</li>
                <li>info@np-productions.com</li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-primary-800 text-center text-sm text-primary-400">
            &copy; {new Date().getFullYear()} NP Productions. All rights reserved.
          </div>
        </div>
      </footer>
    </main>
  );
}
